import * as React from "react"
import { GroupList } from "@/components/groups/group-list"
import { GroupForm } from "@/components/groups/group-form"
import { GroupInvite } from "@/components/groups/group-invite"
import { JoinGroupModal } from "@/components/groups/join-group-modal"
import { InvitationCodesList } from "@/components/groups/invitation-codes-list"
import { ResponsiveContainer } from "@/components/layout/responsive-grid"
import { LoadingState } from "@/components/ui/loading-state"
import { OfflineBadge } from "@/components/offline/offline-indicator"
import { Button } from "@/components/ui/button"
import { UserPlus } from "lucide-react"
import { useAuthStore } from "@/stores/auth.store"
import { useActiveGroup } from "@/stores/group.store"
// Temporarily use standard hooks until offline groups is reimplemented
// import { useOfflineGroups, useCreateOfflineGroup, useUpdateOfflineGroup, useAddMemberOffline } from "@/hooks/offline/useOfflineGroups"
import { useUserGroups } from "@/hooks/queries/useGroups"
import { useCreateGroup, useUpdateGroup, useInviteToGroup, useJoinGroupWithCode } from "@/hooks/mutations/useCreateGroup"
import { useAllUsers } from "@/hooks/queries/useAllUsers"
import { toast } from "sonner"
import type { Group } from "@/types/domain.types"
import { logger } from '@/lib/logger'


export const GroupsPage: React.FC = () => {
  const { user } = useAuthStore()
  const { setActiveGroupId } = useActiveGroup()
  const [showGroupForm, setShowGroupForm] = React.useState(false)
  const [showGroupInvite, setShowGroupInvite] = React.useState(false)
  const [showJoinModal, setShowJoinModal] = React.useState(false)
  const [editingGroup, setEditingGroup] = React.useState<Group | undefined>()
  const [invitingGroup, setInvitingGroup] = React.useState<Group | undefined>()
  const [selectedGroupId, setSelectedGroupId] = React.useState<string>()

  // Fetch groups (using standard API for now)
  const { data: groups = [], isLoading, error } = useUserGroups()

  // Fetch all users for the invite modal
  const { data: allUsers = [] } = useAllUsers()

  // Mutations (using standard API for now)
  const createGroupMutation = useCreateGroup()
  const updateGroupMutation = useUpdateGroup()
  const inviteToGroupMutation = useInviteToGroup()
  const joinGroupMutation = useJoinGroupWithCode()

  const handleCreateGroup = () => {
    setEditingGroup(undefined)
    setShowGroupForm(true)
  }

  const handleGroupSelect = (group: Group) => {
    setSelectedGroupId(group.id)
    setActiveGroupId(group.id)
    // TODO: Navigate to group details
    logger.debug("Selected group:", { group })
  }

  const handleGroupEdit = (group: Group) => {
    setEditingGroup(group)
    setShowGroupForm(true)
  }

  const handleGroupInvite = (group: Group) => {
    setInvitingGroup(group)
    setShowGroupInvite(true)
  }

  const handleGroupSettings = (group: Group) => {
    // TODO: Navigate to group settings
    logger.debug("Group settings:", { group })
  }

  const handleSubmitGroup = async (data: {
    name: string;
    description?: string;
    emoji?: string;
    currency: string;
  }) => {
    try {
      if (editingGroup) {
        // Update existing group with offline support
        const result = await updateGroupMutation.mutateAsync({
          groupId: editingGroup.id,
          name: data.name
          // Note: The backend currently only accepts 'name' parameter
          // TODO: Add description, emoji, and currency when backend supports them
        })

        if (result) {
          setShowGroupForm(false)
          setEditingGroup(undefined)
        }
      } else {
        // Create new group with offline support
        const result = await createGroupMutation.mutateAsync({
          name: data.name,
          // Note: The backend currently only accepts 'name' parameter
          // TODO: Add description, emoji, and currency when backend supports them
        })

        // Set the newly created group as active
        if (result && result.id) {
          setActiveGroupId(result.id)
        }

        // Close the form
        setShowGroupForm(false)
      }
    } catch (error) {
      logger.error(`Failed to ${editingGroup ? 'update' : 'create'} group:`, error)
      // Toast is handled by the mutation hooks
    }
  }

  const handleGenerateInvite = async (): Promise<string> => {
    if (!invitingGroup?.id) {
      toast.error("No group selected")
      return ""
    }

    try {
      const result = await inviteToGroupMutation.mutateAsync(invitingGroup.id)

      let token = ""

      // The mutation returns the invitation object
      if (result && typeof result === 'object' && 'token' in result) {
        token = result.token as string
      } else if (typeof result === 'string') {
        // In case it returns just the token string
        token = result
      }

      if (token) {
        // Copy to clipboard
        try {
          await navigator.clipboard.writeText(token)
          toast.success("Invitation code copied to clipboard!")
        } catch (err) {
          toast.success("Invitation code generated successfully!")
        }

        // Close the modal after a short delay
        setTimeout(() => {
          setShowGroupInvite(false)
        }, 1500)

        return token
      }

      toast.error("Failed to generate invitation code")
      return ""
    } catch (error) {
      logger.error("Failed to generate invite:", error)
      toast.error("Failed to generate invitation code")
      return ""
    }
  }

  const handleRefreshInvite = async (): Promise<string> => {
    // Same as generate - creates a new invitation
    return handleGenerateInvite()
  }

  const handleAddUsersToGroup = async () => {
    if (!invitingGroup?.id) {
      toast.error("No group selected")
      return
    }

    try {
      // Generate an invite code for the group
      const inviteCode = await inviteToGroupMutation.mutateAsync(invitingGroup.id)
      if (inviteCode) {
        toast.success(`Invite code generated: ${inviteCode}`)
      }
    } catch (error) {
      logger.error("Failed to generate invite:", error)
      toast.error("Failed to generate invite code")
    }
  }

  const handleJoinGroup = async (inviteCode: string) => {
    await joinGroupMutation.mutateAsync(inviteCode)
  }

  if (isLoading) {
    return <LoadingState message="Loading groups..." />
  }

  if (error) {
    return (
      <ResponsiveContainer size="xl">
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold text-destructive mb-2">
            Failed to load groups
          </h2>
          <p className="text-muted-foreground">
            Please try refreshing the page
          </p>
        </div>
      </ResponsiveContainer>
    )
  }

  return (
    <ResponsiveContainer size="xl">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Groups</h1>
            <p className="text-muted-foreground">
              Manage your expense groups and invite members
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowJoinModal(true)}
              className="gap-2"
            >
              <UserPlus className="h-4 w-4" />
              Join Group
            </Button>
            <OfflineBadge />
          </div>
        </div>

        <InvitationCodesList
          groups={groups as Group[]}
          onRefreshCode={handleRefreshInvite}
        />

        <GroupList
          groups={groups as Group[]}
          currentUser={user!}
          selectedGroupId={selectedGroupId}
          isLoading={isLoading}
          isError={!!error}
          onGroupSelect={handleGroupSelect}
          onGroupEdit={handleGroupEdit}
          onGroupInvite={handleGroupInvite}
          onGroupSettings={handleGroupSettings}
          onCreateGroup={handleCreateGroup}
        />

        <GroupForm
          open={showGroupForm}
          onOpenChange={setShowGroupForm}
          group={editingGroup}
          onSubmit={handleSubmitGroup}
          isSubmitting={createGroupMutation.isPending || updateGroupMutation.isPending}
        />

        {invitingGroup && (
          <GroupInvite
            open={showGroupInvite}
            onOpenChange={setShowGroupInvite}
            group={invitingGroup}
            onGenerateInvite={handleGenerateInvite}
            onRefreshInvite={handleRefreshInvite}
            onAddUsersToGroup={handleAddUsersToGroup}
            allUsers={allUsers}
          />
        )}

        <JoinGroupModal
          open={showJoinModal}
          onOpenChange={setShowJoinModal}
          onJoinGroup={handleJoinGroup}
          isLoading={joinGroupMutation.isPending}
        />
      </div>
    </ResponsiveContainer>
  )
}